import { useContext, useEffect, useState, useRef } from 'react';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { storeContext } from '../../store/store';
import { HouseholdIds, Session, Token } from '../../models';
import { useAthenaLogger, useConfig, useTransmit } from '..';
import {
  deleteLocalStorage,
  deleteSessionStorage,
  getLocalStorage,
  getSessionStorage,
  setLocalStorage,
  setSessionStorage,
} from '../../utils/StorageUtil';
//TODO: remove this clear cookies and explore if it needs to be clear session properties
import { clearCookies } from '../../utils/CookieUtil';
import { useTransmitRef } from '../../contexts/transmitContext';
import { useBroadcastRef } from '../../contexts/messagingContext';
import { makeGuid } from '../../utils';
import TransmitToken from '../../models/TransmitToken';

const TRANSMIT_REFRESH_INTERVAL_MS = 60000; // Refresh Transmit Token every 60 seconds or as adjusted

/***
 * mainSession: Boolean, Only set this to true in the main session instance in app.tsx to ensure effects only get ran once
 */
const useSession = (mainSession?: boolean) => {
  const { state, dispatch } = useContext(storeContext);
  const { wealthConfig, transmitConfig } = useConfig();
  const [sessionTimer, setSessionTimer] = useState<NodeJS.Timeout>();
  const { refreshTransmitToken, initTransmit, logoutTransmit } = useTransmit();
  const { transmitToken } = useTransmitRef();
  const {
    broadcastRef: { broadcastDispatch },
  } = useBroadcastRef();
  const athenaLogger = useAthenaLogger();
  const refreshTransmitTokenRef = useRef(refreshTransmitToken);

  const proxyInstance: AxiosInstance = axios.create({
    baseURL: wealthConfig.proxyUrl,
    withCredentials: true,
  });

  const {
    investConfig: { interstitialsUrl },
  } = useConfig();

  useEffect(() => {
    refreshTransmitTokenRef.current = refreshTransmitToken;
  });

  useEffect(() => {
    let tmpSessionTimer: NodeJS.Timeout;
    if (mainSession && state.session.loggedIn) {
      if (transmitConfig.useTransmit && !transmitToken.access_token) {
        initTransmit(true);
      }
      refreshSessionTimer();
      tmpSessionTimer = setInterval(() => {
        if (
          new Date() > new Date(getLocalStorage('sessionExpiry') || new Date())
        ) {
          logout();
          sessionTimer && clearInterval(sessionTimer);
        }
      }, 5000);
      setSessionTimer(tmpSessionTimer);
    }
    return () => {
      if (mainSession && sessionTimer) {
        clearInterval(sessionTimer);
      }
    };
  }, [state.session.username]);

  useEffect(() => {
    if (mainSession && !!state.session.loggedIn) {
      const interval = setInterval(() => {
        refreshTransmitTokenRef.current();
      }, TRANSMIT_REFRESH_INTERVAL_MS);
      return () => clearInterval(interval);
    }
  }, [state.session.loggedIn]);

  const refreshSessionTimer = () => {
    if (state.session.loggedIn) {
      const newExpiry = new Date();
      newExpiry.setMinutes(newExpiry.getMinutes() + 15);
      setLocalStorage('sessionExpiry', newExpiry);
      getOrionToken();
    }
  };

  const loginWithTransmitToken = async (
    transmitToken: TransmitToken
  ): Promise<void> => {
    try {
      deleteLocalStorage('logout');

      const sessionResponse: AxiosResponse<Session> = await proxyInstance.post(
        '/identity/sso',
        { token: transmitToken },
        {
          headers: {
            'Content-Type': 'application/json',
            correlationid: makeGuid(),
          },
        }
      );

      const loggedIn = sessionResponse.status === 200;
      athenaLogger.sendLog(
        `*LOGINWITHTRANSMITTOKEN* Logged In: ${loggedIn}`,
        'info'
      );
      const {
        username,
        clientID,
        sviuserid: sviuserid,
        guid,
        householdIds,
        processingInterstitials,
      } = sessionResponse.data;

      let orionToken: Token;

      if (sessionResponse.data.orionToken) {
        orionToken = new Token(
          sessionResponse.data.orionToken.token,
          '',
          sessionResponse.data.orionToken.expiresIn,
          sessionResponse.data.orionToken.tokenType,
          false
        );
      } else {
        orionToken = new Token();
      }

      const newSession: Session = new Session(
        loggedIn,
        username,
        clientID,
        sviuserid,
        guid,
        orionToken,
        householdIds
      );

      refreshSessionTimer();
      dispatch({ type: 'LOGIN', payload: newSession });

      if (processingInterstitials) {
        return window.location.assign(interstitialsUrl);
      }
    } catch (error) {
      if (error instanceof Error) {
        athenaLogger.sendLog(
          `*LOGIN* Error In User Login: ${error.message}`,
          'error'
        );
      }
    }
  };

  const loginWithCreds = async (
    userId: string,
    password: string
  ): Promise<void> => {
    try {
      deleteLocalStorage('logout');

      const sessionResponse: AxiosResponse<Session> = await proxyInstance.post(
        '/identity/login',
        { username: userId, password: password },
        {
          headers: {
            'Content-Type': 'application/json',
            correlationid: makeGuid(),
          },
        }
      );

      const loggedIn = sessionResponse.status === 200;
      athenaLogger.sendLog(`*LOGINWITHCREDS* Logged In: ${loggedIn}`, 'info');

      const {
        username,
        clientID,
        sviuserid: sviuserid,
        guid,
        householdIds,
      } = sessionResponse.data;

      let orionToken: Token;

      if (sessionResponse.data.orionToken) {
        orionToken = new Token(
          sessionResponse.data.orionToken.token,
          '',
          sessionResponse.data.orionToken.expiresIn,
          sessionResponse.data.orionToken.tokenType,
          false
        );
      } else {
        orionToken = new Token();
      }

      const newSession: Session = new Session(
        loggedIn,
        username,
        clientID,
        sviuserid,
        guid,
        orionToken,
        householdIds
      );
      refreshSessionTimer();
      dispatch({ type: 'LOGIN', payload: newSession });
    } catch (error) {
      if (error instanceof Error) {
        athenaLogger.sendLog(
          `*LOGIN* Error In User Login: ${error.message}`,
          'error'
        );
      }
    }
  };

  /**
   * Function used to update a prospects session to get their household ids and orion token
   * now that they have a wealth relationship
   */
  const updateProspectSession = async (): Promise<void> => {
    try {
      const householdIds =
        ((await getHouseholdId()) as HouseholdIds) || new HouseholdIds();
      const orionToken = (await getOrionToken()) || new Token();
      dispatch({
        type: 'LOGIN',
        payload: { ...state.session, householdIds, orionToken },
      });
    } catch (error) {
      athenaLogger.sendLog(
        '*UPDATEPROSPECTSESSION* Error Updating Prospect Session'
      );
    }
  };

  const getOrionToken = async (): Promise<Token> => {
    try {
      dispatch({ type: 'SET_ORION_TOKEN_IS_LOADING', payload: true });
      const {
        data: token,
      }: AxiosResponse<{
        token: string;
        expiresIn: number;
        expiresAt: Date;
        tokenType: string;
      }> =
        ((await proxyInstance.get(`/orion/token`).catch(() => {
          dispatch({ type: 'SET_ORION_TOKEN_IS_LOADING', payload: false });
        })) as AxiosResponse) || {};
      if (token) {
        return new Token(
          token.token,
          '',
          token.expiresIn,
          token.tokenType,
          false
        );
      }
      return new Token();
    } catch (error) {
      if (error instanceof Error) {
        athenaLogger.sendLog(
          '*ORIONTOKEN* Error Getting Orion Token From Session',
          'error'
        );
      }
      return new Token();
    }
  };

  const getHouseholdId = async () => {
    try {
      const householdId = state.session.householdIds;
      athenaLogger.sendLog(
        `*HOUSEHOLDID* Got HouseHold ID: ${householdId}`,
        'info'
      );
      if (householdId) {
        return householdId;
      }
    } catch (error) {
      if (error instanceof Error) {
        athenaLogger.sendLog(
          '*HOUSEHOLD ID* Error Getting householdId from Session',
          'error'
        );
      }
      return new Token();
    }
  };

  const logout = async () => {
    await clearSession();
    window.location.href = '/logout';
  };

  const clearSession = async () => {
    await proxyInstance.get('/identity/logout');
    deleteLocalStorage('sessionExpiry');
    deleteSessionStorage('WEALTH-TS');
    clearCookies();
    broadcastDispatch({ type: 'LOGOUT' }, true);
    dispatch({ type: 'BEGIN_LOGOUT', payload: false });
    await logoutTransmit();
  };

  return {
    session: state.session,
    isLoggedIn: state.session.loggedIn,
    loginWithCreds,
    loginWithTransmitToken,
    updateProspectSession,
    logout,
    clearSession,
    getOrionToken,
    orionToken: state.session.orionToken,
    getHouseholdId,
    getSessionStorage,
    setSessionStorage,
    deleteSessionStorage,
    refreshSessionTimer,
    getLocalStorage,
    setLocalStorage,
    deleteLocalStorage,
    transmitToken,
  };
};

export default useSession;
